import Api from "../../../../../../../../core/services/api/api";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toImportCodeUpper, toPartNumberUpper} from "./shared";
import {
    applyQtySerialRules,
    parseDelimitedList,
    parseNonNegativeNumber,
    toDistinctSerials,
} from "./shared/inventory-availability-validation";

/**
 * Sets warehouse on-hand quantities via Inventory/SetInventoryAvailability (batched by warehouse).
 */
export class SetInventoryAvailabilityImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {{ warehouses?: Array<*>, serialImportSplitter?: string }} [options]
     */
    async run(mappedData, isFinalRun = false, options = {}) {
        const {warehouses = null, serialImportSplitter = ','} = options;
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, warehouseCodeUpper: string, quantity: number, avgCost: number, serialNumbers: string[], distinctSerials: string[], codes: string[]}[]} */
        const rowState = rows.map((item, index) => {
            const serialNumbers = parseDelimitedList(item?.serialNumbers, serialImportSplitter);
            return {
                index,
                item,
                partNoUpper: toPartNumberUpper(item?.partNo),
                warehouseCodeUpper: toImportCodeUpper(item?.warehouseCode),
                quantity: parseNonNegativeNumber(item?.quantity),
                avgCost: parseNonNegativeNumber(item?.avgCost),
                serialNumbers,
                distinctSerials: toDistinctSerials(serialNumbers),
                codes: [],
            };
        });

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (!rs.partNoUpper || !rs.warehouseCodeUpper) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
            if (Number.isNaN(rs.quantity)) {
                rs.codes.push(PreventionInvalidityCode.INVALID_AMOUNT);
            }
            if (Number.isNaN(rs.avgCost)) {
                rs.codes.push(PreventionInvalidityCode.INVALID_AMOUNT);
            }
        }

        let warehouseList = warehouses;
        if (!warehouseList?.length) {
            const warehousesResponse = await Api.getOperatorWarehouses();
            warehouseList = warehousesResponse?.data ?? [];
        }
        const warehouseMap = Object.fromEntries(
            (warehouseList ?? []).map((w) => [String(w.code).toUpperCase(), w]),
        );

        const uniqueParts = [
            ...new Set(
                rowState
                    .filter((r) => r.item && r.partNoUpper && r.warehouseCodeUpper)
                    .map((r) => r.partNoUpper),
            ),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.warehouseCodeUpper) {
                continue;
            }
            if (!partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
            if (!warehouseMap[rs.warehouseCodeUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_WAREHOUSE);
            }
        }

        for (const rs of rowState) {
            if (!rs.item || Number.isNaN(rs.quantity)) {
                continue;
            }
            rs.codes.push(
                ...applyQtySerialRules({
                    qty: rs.quantity,
                    distinctSerialCount: rs.distinctSerials.length,
                }),
            );
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        /** @type {Record<string, { whse: string, items: Object[], rowIndices: number[] }>} */
        const batchesByWarehouse = {};

        rows.forEach((item, index) => {
            if (!item || issueIndexSet.has(index)) {
                return;
            }

            const rs = rowState[index];
            const quantity = Number.isNaN(rs.quantity) ? null : rs.quantity;
            const avgCost = Number.isNaN(rs.avgCost) ? null : rs.avgCost;
            const hasSerials = rs.distinctSerials.length > 0;

            if (isFinalRun && (quantity == null || avgCost == null || !rs.warehouseCodeUpper || !rs.partNoUpper)) {
                return;
            }

            const whseKey = rs.warehouseCodeUpper;
            if (!whseKey) {
                return;
            }
            if (!batchesByWarehouse[whseKey]) {
                batchesByWarehouse[whseKey] = {
                    whse: whseKey,
                    items: [],
                    rowIndices: [],
                };
            }

            batchesByWarehouse[whseKey].items.push({
                partNo: rs.partNoUpper,
                qty: quantity != null ? String(quantity) : '',
                averageCost: avgCost != null ? String(avgCost) : '',
                serialNumbers: hasSerials ? rs.distinctSerials : [],
            });
            batchesByWarehouse[whseKey].rowIndices.push(index);
        });

        const apiRequests = Object.values(batchesByWarehouse);

        return {apiRequests, preventativeIssues};
    }
}
